/* ==========================================================================
   ActivoHub — Motion controller
   One IntersectionObserver for the whole page. Elements are unobserved once
   revealed, so there is no ongoing scroll work. No animation library.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Which elements reveal on scroll. Chosen deliberately:
     section-level content, not every paragraph — over-animating is what makes
     a site feel busy rather than smooth. */
  var REVEAL = [
    '.section > .two',
    '.section .callouts',
    '.section > .eyebrow',
    '.section > h2',
    '.table-scroll',
    '.founder',
    '.reviews-pending',
    '.proofbar ul',
    '.spec-grid',
    '.honest',
    '.pcard',
    '.empty'
  ].join(',');

  var STAGGER = ['.cards', '.fitlist', '.microtrust'].join(',');

  function markup() {
    // Hero is excluded — it animates on load, not on scroll.
    document.querySelectorAll(REVEAL).forEach(function (el) {
      if (el.closest('.hero') || el.closest('.drawer') || el.hasAttribute('data-reveal')) return;
      el.setAttribute('data-reveal', '');
    });
    document.querySelectorAll(STAGGER).forEach(function (el) {
      if (el.closest('.hero') || el.closest('.drawer')) return;
      el.setAttribute('data-reveal', '');
      el.setAttribute('data-reveal-stagger', '');
    });
  }

  /* Abort motion entirely and leave everything visible. */
  function disableMotion() {
    document.documentElement.classList.remove('ah-motion');
  }

  function observe() {
    var targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) { disableMotion(); return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.classList.add('is-in');
        io.unobserve(el);                       // one-shot: never animates twice
        setTimeout(function () { el.classList.add('is-done'); }, 700);
      });
    }, {
      // Fire slightly before the element reaches the viewport so the motion
      // has finished by the time it is properly in view.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.05
    });
    targets.forEach(function (el) { io.observe(el); });

    // Anything already on screen at load reveals immediately, so the first
    // paint is never half-faded.
    requestAnimationFrame(function () {
      targets.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          el.classList.add('is-in');
          io.unobserve(el);
        }
      });
    });

    /* FAILSAFE — unconditional.

       If the observer never fires (a rendering quirk, a throttled background
       tab, a browser that reports support but misbehaves), content must not
       stay invisible. After 3s every remaining target is revealed regardless
       of scroll position.

       Elements still below the fold are off-screen anyway, so revealing them
       early costs the viewer nothing — while a section that never appears
       would cost a sale. The animation is an enhancement; the content is not
       allowed to depend on it. */
    setTimeout(function () {
      var stuck = 0;
      targets.forEach(function (el) {
        if (!el.classList.contains('is-in')) { el.classList.add('is-in'); stuck++; }
      });
      // If literally nothing had revealed, the observer is not working here —
      // stop hiding future content altogether.
      if (stuck === targets.length) disableMotion();
    }, 3000);
  }

  /* Header shadow once scrolled */
  function stickyHeader() {
    var header = document.querySelector('.header');
    if (!header) return;
    var queued = false;
    function update() { queued = false; header.classList.toggle('is-stuck', window.scrollY > 8); }
    window.addEventListener('scroll', function () {
      if (!queued) { queued = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* Cart badge pulse when the count changes */
  function cartBadge() {
    var badge = document.querySelector('[data-cart-count]');
    if (!badge || !('MutationObserver' in window)) return;
    var last = badge.textContent;
    new MutationObserver(function () {
      if (badge.textContent === last) return;
      last = badge.textContent;
      badge.classList.remove('is-bumped');
      void badge.offsetWidth;                   // restart the animation
      badge.classList.add('is-bumped');
    }).observe(badge, { childList: true, characterData: true, subtree: true });
  }

  /* Gallery: dip the main image while the new source loads, so the swap
     reads as a transition instead of a flash. */
  function gallery() {
    var thumbs = document.getElementById('thumbs');
    var main = document.getElementById('hero-img');
    if (!thumbs || !main) return;
    thumbs.addEventListener('click', function (e) {
      if (!e.target.closest('button')) return;
      main.classList.add('is-swapping');
      var done = function () { main.classList.remove('is-swapping'); main.removeEventListener('load', done); };
      main.addEventListener('load', done);
      setTimeout(done, 400);                    // safety net for cached images
    });
  }

  function init() {
    // Hover, press and micro-detail motion is pure CSS and always safe.
    // Only scroll-reveal hides content, so only that is gated.
    stickyHeader();
    cartBadge();
    gallery();

    if (reduced) return;
    if (!('IntersectionObserver' in window)) return;   // content stays visible

    try {
      markup();
      document.documentElement.classList.add('ah-motion');
      observe();
    } catch (e) {
      disableMotion();                                  // never trade content for polish
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
})();
